import React from 'react';

import { useSnackbar } from 'notistack';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Alert, Box, Button, IconButton, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';

import { Event } from '~/services/CorporateEventsService';

interface EventFormHeaderProps {
  tab: string;
  event?: Event;
  onBack: () => void;
  onTabChange: (tab: string) => void;
  hasUnsavedChanges?: boolean;
  isCreateMode?: boolean;
}

export default function EventFormHeader({
  tab,
  event,
  onBack,
  onTabChange,
  hasUnsavedChanges = false,
  isCreateMode = false,
}: EventFormHeaderProps) {
  const { enqueueSnackbar } = useSnackbar();
  const title = event?.id ? `Edit Event: ${event.name}` : 'Create Event';
  const shouldBlockTabs = isCreateMode || hasUnsavedChanges;

  const registrationUrl =
    event?.id && window.configs?.['LUX_CUSTOMER_PORTAL']
      ? `${window.configs['LUX_CUSTOMER_PORTAL']}/corporate-events/${event.id}/register`
      : null;

  const handleCopyLink = async () => {
    if (!registrationUrl) {
      return;
    }
    try {
      await navigator.clipboard.writeText(registrationUrl);
      enqueueSnackbar('Registration link copied to clipboard', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to copy link', { variant: 'error' });
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newTab: string) => {
    if (shouldBlockTabs && newTab !== 'details') {
      return; // Prevent tab change
    }
    onTabChange(newTab);
  };

  return (
    <>
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" gap={2} alignItems="center">
          <Button variant="text" startIcon={<ArrowBackIcon />} onClick={onBack}>
            Back to List
          </Button>
          <Box component="h2" m={0}>
            {title}
          </Box>
        </Box>
        {registrationUrl && (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              Registration Link:
            </Typography>
            <TextField
              size="small"
              value={registrationUrl}
              slotProps={{ input: { readOnly: true } }}
              sx={{ width: 400 }}
            />
            <Tooltip title="Copy to clipboard">
              <IconButton onClick={handleCopyLink} size="small" color="primary">
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
      {shouldBlockTabs && tab === 'details' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {isCreateMode
            ? 'Please save the event details first before accessing other tabs.'
            : 'Please save your changes before accessing other tabs.'}
        </Alert>
      )}
      <Box borderBottom={1} borderColor="divider" component="div">
        <Tabs value={tab} onChange={handleTabChange}>
          <Tab value="details" label="Event Details" />
          <Tab value="custom-questions" label="Custom Questions" disabled={shouldBlockTabs} />
          {event && <Tab value="invited-users" label="Invited Users" disabled={shouldBlockTabs} />}
          {event && <Tab value="data-management" label="Data Management" disabled={shouldBlockTabs} />}
        </Tabs>
      </Box>
    </>
  );
}
